import { BaseWebComponentController, IListenerConfig } from '../../../../framework/BaseWebComponentController';
import { IntegrityMonitorDefaultRuleConfigurationWebComponent } from '../component/IntegrityMonitorDefaultRuleConfigurationWebComponent';
import { IntegrityMonitorDefaultRuleConfigurationView } from '../view/IntegrityMonitorDefaultRuleConfigurationView';
import { templateImDefaultRuleConfiguration } from '../view/template';
import {
  IntegrityMonitorConfigurationStatus,
  IntegrityMonitorRuleEntity,
  IntegrityMonitorRuleGridItem,
} from '../../integrityMonitorRulesGrid/types';
import { htmlEncode } from '../../../../utils/escape.utils';
import { getFormattedDateTime, TimestampFormat } from '@nirvana/shared/time';
import { getIntegrityMonitorTypeValues } from '../../../common/utils/types';
import { DefaultRuleConfigurationEvents, IntegrityMonitorAssignationData } from '../types';
import { GzLinkWebComponent } from '../../../../ui/gzLinkComponent';
import { LinkColor } from '../../../../ui/gzLinkComponent/types';
import { FontSizes } from '../../../../types/common/types';
import { CommonEvents } from '@nirvana/shared/constants';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { GzHardCodedFormField } from '../../../../ui/gzForm/formField/component/GzHardCodedFormField';
import { IntegrityMonitorDefaultRuleConfigurationRepository } from '../repository/IntegrityMonitorDefaultRuleConfigurationRepository';
import { FormListItem } from '../../../../ui/gzForm/formList/types/FormListItem';
import { getSeverityWithBullet } from '../../../common/utils/helpers';
import { GzTextWebComponent } from '../../../../ui/gzText';
import { showErrorToast, showSuccessToast } from '@nirvana/shared/common';
import { IMConstants } from '../../../common/utils/IMConstants';

export class IntegrityMonitorDefaultRuleConfigurationController extends BaseWebComponentController {
  public component!: IntegrityMonitorDefaultRuleConfigurationWebComponent;
  public view!: IntegrityMonitorDefaultRuleConfigurationView;
  public viewConstructor!: typeof IntegrityMonitorDefaultRuleConfigurationView;
  public dataRepository!: IntegrityMonitorDefaultRuleConfigurationRepository;
  public dataRepositoryConstructor!: typeof IntegrityMonitorDefaultRuleConfigurationRepository;

  constructor(component: IntegrityMonitorDefaultRuleConfigurationWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: IntegrityMonitorDefaultRuleConfigurationView,
      viewTemplate: templateImDefaultRuleConfiguration,
      dataRepositoryConstructor: IntegrityMonitorDefaultRuleConfigurationRepository,
    });
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$saveButton,
        event: 'click',
        callback: this.saveCustomizedRule,
      },
      {
        element: this.view.dom.$cancelButton,
        event: 'click',
        callback: this.closeCustomizationForm,
      },
    ];
  }

  public async setRuleData(ruleData: IntegrityMonitorRuleGridItem) {
    if (!this.component.started) {
      return;
    }

    this.fillFormGroup(ruleData);
    this.setGeneralSectionData(ruleData);
    this.setDetailsSectionData(ruleData);
    this.setConfigurationSection(ruleData);
    await this.setAssignationSectionData(ruleData);

    this.view.removeLoadingState();
  }

  private fillFormGroup(ruleData: IntegrityMonitorRuleGridItem): void {
    if (!this.view.formGroup) {
      this.view.formGroup = new FormGroup({});
    }

    this.resetForm();

    for (const [field, value] of Object.entries(ruleData)) {
      if (field !== 'stringVariables') {
        this.view.formGroup.addFormChild([field], new GzHardCodedFormField(value));
      }
    }
  }

  private setGeneralSectionData(ruleData: IntegrityMonitorRuleGridItem): void {
    const timezone = this.dependencies.getTimezone();
    const locale = this.dependencies.getUserLanguage();
    const lastModifiedOn = getFormattedDateTime(
      TimestampFormat.EVENTS,
      +new Date(ruleData.lastModifiedOn),
      timezone,
      locale
    );
    let createdOn = this.dependencies.translateFn('N/A');

    if (ruleData.createdOn) {
      createdOn = getFormattedDateTime(TimestampFormat.EVENTS, +new Date(ruleData.createdOn), timezone, locale);
    }

    const { bullet, severityLabel } = getSeverityWithBullet(this.dependencies.translateFn, ruleData.severity);

    this.view.dom.$ruleName.innerHTML = `<gz-text fontSize='13'>${htmlEncode(ruleData.ruleName)}</gz-text>`;
    this.view.dom.$ruleDescription.innerHTML = `<gz-text fontSize='13'>${htmlEncode(
      ruleData.description ?? '-'
    )}</gz-text>`;
    this.view.dom.$ruleSeverity.innerHTML = `<gz-text class='has-bullet ${bullet}' fontSize='13'>${htmlEncode(
      severityLabel
    )}</gz-text>`;
    this.view.dom.$ruleType.innerHTML = `<gz-text fontSize='13'>${htmlEncode(
      ruleData.isDefaultRule ? this.dependencies.translateFn('Default') : this.dependencies.translateFn('Custom')
    )}</gz-text>`;
    this.view.dom.$ruleCreatedOn.innerHTML = `<gz-text fontSize='13'>${htmlEncode(createdOn)}</gz-text>`;
    this.view.dom.$ruleModifiedOn.innerHTML = `<gz-text fontSize='13'>${htmlEncode(lastModifiedOn)}</gz-text>`;
  }

  private setDetailsSectionData(ruleData: IntegrityMonitorRuleGridItem): void {
    const entitiesData = ruleData.entities;
    const entityTypesSet = new Set();

    for (const entity of entitiesData) {
      entityTypesSet.add(entity.entityType);
    }

    let entityTypesList = [...entityTypesSet];

    entityTypesList = entityTypesList.map(entityType => {
      return getIntegrityMonitorTypeValues(this.dependencies.translateFn).find(element => element.value === entityType)
        ?.label;
    });

    this.view.dom.$ruleEntityTypes.innerHTML = `<gz-text fontSize='13'>${htmlEncode(
      entityTypesList.join(', ')
    )}</gz-text>`;

    this.setMitreIdsHtml(ruleData.mitreIds);
  }

  private setMitreIdsHtml(mitreIds: Array<string>) {
    if (mitreIds.length <= 1) {
      this.view.dom.$mitreIds.label = this.dependencies.translateFn('MITRE ATT&CK ID');
    } else {
      this.view.dom.$mitreIds.label = this.dependencies.translateFn('MITRE ATT&CK IDs');
    }

    if (mitreIds.length === 0) {
      this.view.dom.$mitreIds.innerHTML = `<gz-text fontSize='13'>${this.dependencies.translateFn('N/A')}</gz-text>`;
      return;
    }

    mitreIds.forEach((mitreId, indexMitreId) => {
      const mitreIdLinkWebComponent = new GzLinkWebComponent();
      const mitreURIResource = mitreId.replace('.', '/');

      mitreIdLinkWebComponent.href = `${IMConstants.URL_ATTACK_MITRE_TECHNIQUES}/${mitreURIResource}`;
      mitreIdLinkWebComponent.fontSize = FontSizes.PARAGRAPH_13;
      mitreIdLinkWebComponent.color = LinkColor.PRIMARY;
      mitreIdLinkWebComponent.innerText = htmlEncode(mitreId);
      mitreIdLinkWebComponent.showIcon = false;

      this.view.dom.$mitreIds.append(mitreIdLinkWebComponent);

      if (indexMitreId < mitreIds.length - 1) {
        this.view.dom.$mitreIds.append(', ');
      }
    });
  }

  private setConfigurationSection(ruleData: IntegrityMonitorRuleGridItem): void {
    if (isEmpty(ruleData.stringVariables)) {
      this.view.setFormConfigurability(false);

      return;
    }

    const variablesAreReadOnly =
      ruleData.configurationStatus === IntegrityMonitorConfigurationStatus.STATUS_SOURCE_DELETED ||
      !this.dependencies.canModifyRulesAndRuleSets();

    for (const stringVariable of ruleData.stringVariables) {
      const formInput: FormListItem = {
        id: stringVariable.key,
        name: stringVariable.key,
        placeholder: this.dependencies.translateFn('Type here'),
        value: stringVariable.value,
        required: true,
        disabled: variablesAreReadOnly,
      };

      this.view.dom.$configurationFormFields.addFormListItem(formInput);
    }

    this.view.setFormConfigurability(true, variablesAreReadOnly);
  }

  private async setAssignationSectionData(ruleData: IntegrityMonitorRuleGridItem) {
    let assignationData: IntegrityMonitorAssignationData = {
      policies: [],
      ruleSets: [],
    };

    if (!isEmpty(ruleData.ruleSetIds)) {
      assignationData = await this.dataRepository.getRuleAssignationData(ruleData.ruleSetIds);
    }

    let numPoliciesLeft = assignationData.policies.length ? assignationData.policies.length : -1;
    let numRuleSetsLeft = assignationData.ruleSets.length ? assignationData.ruleSets.length : -1;

    for (const policy of assignationData.policies.slice(0, 3)) {
      const policyLink = new GzLinkWebComponent();
      policyLink.callback = this.sendShowPolicyEvent.bind(this, policy._id);
      policyLink.fontSize = FontSizes.PARAGRAPH_13;
      policyLink.color = LinkColor.PRIMARY;
      policyLink.innerText = policy.name;
      policyLink.showIcon = false;

      this.view.dom.$policies.append(policyLink);
      if (--numPoliciesLeft) {
        this.view.dom.$policies.append(htmlEncode(', '));
      }
    }

    for (const ruleSet of assignationData.ruleSets.slice(0, 3)) {
      const ruleSetLink = new GzLinkWebComponent();
      ruleSetLink.callback = this.sendShowRuleSetEvent.bind(this, ruleSet._id);
      ruleSetLink.fontSize = FontSizes.PARAGRAPH_13;
      ruleSetLink.color = LinkColor.PRIMARY;
      ruleSetLink.innerText = ruleSet.name;
      ruleSetLink.showIcon = false;

      this.view.dom.$ruleSets.append(ruleSetLink);
      if (--numRuleSetsLeft) {
        this.view.dom.$ruleSets.append(htmlEncode(', '));
      }
    }

    if (numPoliciesLeft < 0) {
      this.view.dom.$policies.innerHTML = `<gz-text fontSize='13'>${htmlEncode(
        this.dependencies.translateFn('N/A')
      )}</gz-text>`;
    } else if (numPoliciesLeft) {
      const policiesMoreText = new GzTextWebComponent();
      policiesMoreText.fontSize = FontSizes.PARAGRAPH_13;
      policiesMoreText.innerText = this.dependencies.translateFn(' and {numPoliciesLeft} more', {
        numPoliciesLeft,
      });
      this.view.dom.$policies.append(policiesMoreText);
    }

    if (numRuleSetsLeft < 0) {
      this.view.dom.$ruleSets.innerHTML = `<gz-text fontSize='13'>${htmlEncode(
        this.dependencies.translateFn('N/A')
      )}</gz-text>`;
    } else if (numRuleSetsLeft) {
      const ruleSetsMoreText = new GzTextWebComponent();
      ruleSetsMoreText.fontSize = FontSizes.PARAGRAPH_13;
      ruleSetsMoreText.innerText = this.dependencies.translateFn(' and {numRuleSetsLeft} more', {
        numRuleSetsLeft,
      });
      this.view.dom.$ruleSets.append(ruleSetsMoreText);
    }
  }

  private sendShowPolicyEvent(policyId: string): void {
    this.closeCustomizationForm();

    this.component.dispatchEvent(
      new CustomEvent(CommonEvents.CLICK_ON_POLICY_NAME, {
        detail: {
          policyId,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private sendShowRuleSetEvent(ruleSetId: string): void {
    this.component.dispatchEvent(
      new CustomEvent(CommonEvents.CLICK_ON_RULE_SET_NAME, {
        detail: {
          ruleSetId,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private async saveCustomizedRule() {
    if (!this.view.formGroup.valid) {
      this.view.formGroup.validate();
      return;
    }

    this.view.dom.$saveButton.showSpinner = true;
    this.view.dom.$saveButton.disabled = true;

    const record: Object = this.view.formGroup.value;

    record['companyId'] = this.dependencies.getActiveCompany().companyId;
    record['stringVariables'] = record['stringVariables'].map(stringVariable => {
      return {
        key: stringVariable.name,
        value: stringVariable.value,
      };
    });

    try {
      await this.dataRepository.configureDefaultRule(record as IntegrityMonitorRuleGridItem);
      showSuccessToast(this.dependencies.translateFn('Rule configured successfully.'));

      this.closeCustomizationForm();

      // Notify the grid for data reloading
      this.component.dispatchEvent(
        new CustomEvent(DefaultRuleConfigurationEvents.DEFAULT_RULE_SAVED, {
          bubbles: true,
        })
      );
    } catch (error) {
      // if the exception is AuthenticationSecurityException it is already treated by the general session exception
      if (error?.exceptionType === 'Exceptions\\AuthenticationSecurityException') {
        return;
      }

      showErrorToast(error);
    } finally {
      // remove the spinner
      this.view.dom.$saveButton.showSpinner = false;
      this.view.dom.$saveButton.disabled = false;
    }
  }

  public closeCustomizationForm(): void {
    this.resetForm();
    this.view.unsubscribeObservers();
    this.component.isVisible = false;
  }

  private resetForm(): void {
    this.view.dom.$configurationFormFields.value = [];
    this.view.formGroup.value = {};

    while (this.view.dom.$policies.firstChild) {
      this.view.dom.$policies.removeChild(this.view.dom.$policies.firstChild);
    }

    while (this.view.dom.$ruleSets.firstChild) {
      this.view.dom.$ruleSets.removeChild(this.view.dom.$ruleSets.firstChild);
    }

    this.view.dom.$mitreIds.textContent = '';
  }
}
